import FacebookLeadAdsForm from 'cio/models/facebook-lead-ads-form';
import CustomForm from 'cio/models/custom-form';

export enum FormType {
  // Facebook Lead Ads forms are fully separate from other forms, have unique setup and processing logic:
  // they're handled by a dedicated "facebook_lead_ads" service
  FacebookLeadAds = 'facebook_lead_ads',

  // All other form types have similar configuration structure and processing logic:
  // They're all handled by the shared "custom_forms" service:
  Custom = 'custom',
  Typeform = 'typeform',
  Jotform = 'jotform',
}

export type FormDetailsURLFormType = 'facebook' | 'custom' | 'typeform';

// RESERVED_FORM_FIELD_NAMES are conflicting with "form_submit" event properties, should be ignored during form scanning and when processing form submissions:
export const RESERVED_FORM_FIELD_NAMES = Object.freeze([
  'form_id',
  'form_name',
  'form_type',
  'form_url',
  'form_url_params',
]);

export function isFacebookForm(form: FacebookLeadAdsForm | CustomForm): form is FacebookLeadAdsForm {
  return form instanceof FacebookLeadAdsForm;
}

export function isCustomForm(form: FacebookLeadAdsForm | CustomForm): form is CustomForm {
  return form instanceof CustomForm;
}

export interface SharedFormModelInterface {
  id: string;
  formId: string;
  formIdLowercase: string;
  formType: FormType;
  name: string;
}

// Generate scanned form HTML fingerprint, mirroring back-end code
// used for detecting potential duplicate forms:
export async function generateFormHTMLFingerprint(htmlFormInputs: string[]) {
  let normalizedHTMLFormInputs = htmlFormInputs.uniq().sort(); // ignore multi-value fields and do a basic sort: mirroring back-end logic
  let message = normalizedHTMLFormInputs.join(';');

  // https://stackoverflow.com/a/63499899
  return Array.prototype.map
    .call(
      new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(message))),
      (x: any) => ('0' + x.toString(16)).slice(-2), // eslint-disable-line
    )
    .join('');
}

export function formTypeIcon(formType: FormType) {
  return {
    [FormType.FacebookLeadAds]: 'facebook',
    [FormType.Custom]: 'form',
    [FormType.Typeform]: 'typeform',
    [FormType.Jotform]: 'jotform',
  }[formType];
}

export function formTypeIconDefaultFill(formType: FormType) {
  return {
    [FormType.FacebookLeadAds]: 'auto',
    [FormType.Custom]: 'muted',
    [FormType.Typeform]: 'auto',
    [FormType.Jotform]: 'auto',
  }[formType];
}

export function formTypeLabel(formType: FormType) {
  return {
    [FormType.FacebookLeadAds]: 'Facebook Lead Ads',
    [FormType.Custom]: 'Custom',
    [FormType.Typeform]: 'Typeform',
    [FormType.Jotform]: 'Jotform',
  }[formType];
}

export function formDetailsURLFormType(form: SharedFormModelInterface): FormDetailsURLFormType {
  return {
    [FormType.FacebookLeadAds]: 'facebook',
    [FormType.Custom]: 'custom',
    [FormType.Typeform]: 'typeform',
    [FormType.Jotform]: 'jotform',
  }[form.formType] as FormDetailsURLFormType;
}

export function formDetailsPageModels(form: SharedFormModelInterface): [FormDetailsURLFormType, string] {
  return [formDetailsURLFormType(form), form.id];
}
